/**
 * Rhode Island Home Nursing Care Provider / Home Care Provider Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: 216-RICR-40-10-17 (Licensing Home Nursing Care Providers and Home Care Providers)
 *   Last amended: May 6, 2024
 * - Statutory Authority: RIGL § 23-17-10, § 23-17-42
 * - Criminal Background Check: RIGL § 23-17-34
 * - Elder Abuse Reporting: RIGL § 42-66-8 (Office of Healthy Aging)
 * - Facility Abuse Reporting: RIGL § 23-17.8-2
 * 
 * LICENSE TYPES (Per 216-RICR-40-10-17 § 17.3):
 * - Home Nursing Care Provider: Provides skilled nursing services (RN/LPN) and may include home care services
 * - Home Care Provider: Provides personal care/homemaker services requiring RN supervision but NOT direct nursing
 * 
 * REGULATORY BODY:
 * Rhode Island Department of Health (RIDOH)
 * Center for Health Facilities Regulation (CHFR)
 * Capitol Hill, Room 306
 * Providence, RI 02908
 * Phone: (401) 222-5960
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - License expires December 31st annually per § 17.4.3(D)
 * - Certificate of Need required for new agencies per § 17.4.1(B)
 * - Accreditation within 2 years of initial license per § 17.5.1(C)(8)
 * - Record retention: 7 years after discharge per § 17.6.5(C)
 * - Background check: Prior to or within 1 week of hire per RIGL § 23-17-34
 * - Insurance: $10,000 bonding + $500,000 liability per § 17.5.1(C)(2)
 * - Homemaker training: 20 hrs classroom + 5 hrs practical per § 17.5.3(I)
 * - Nursing Assistant supervision: Every 3 months per § 17.6.6(B)(3)
 * - Plan of care review: Every 60 days per § 17.8.1(C)(3)
 * - Professional Advisory Committee: Meets 2x/year per § 17.8.1(A)
 * - Elder abuse reporting: RIGL § 42-66-8 (Office of Healthy Aging: 401-462-0555)
 * - Annual performance evaluation within 45 days per § 17.5.3(C)(1)
 * 
 * Last Verified: December 2024 against 216-RICR-40-10-17
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const RHODEISLAND_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'RI-A1',
    itemId: 'A1',
    stateCode: 'RI',
    title: 'Home Care Provider License Status',
    description: 'Rhode Island requires licensure per 216-RICR-40-10-17 and RIGL § 23-17-4',
    severity: 'high',
    regulationCode: '17.4.1',
    regulationName: 'License',
    agencyName: 'Rhode Island Department of Health - Center for Health Facilities Regulation',
    agencyAbbr: 'RIDOH-CHFR',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'What type of RIDOH license does your agency hold?',
        inputType: 'select',
        options: [
          { value: 'home_nursing', label: 'Home Nursing Care Provider (skilled nursing services)' },
          { value: 'home_care', label: 'Home Care Provider (personal care/homemaker only)' },
          { value: 'both', label: 'Both license types' },
          { value: 'none', label: 'No current license' },
        ],
        required: true,
        helpText: 'Per § 17.3: Home Nursing Care = RN/LPN direct nursing. Home Care = personal care/homemaker services under RN supervision.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Per § 17.4.3(D): License expires December 31st annually.',
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'none',
        },
      },
      {
        id: 'q3',
        question: 'Is your license posted conspicuously in your central office?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 17.4.3(F): License shall be kept posted in a conspicuous place on licensed premises.',
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'none',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      // License not posted is AT_RISK per § 17.4.3(F)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your RIDOH license is current per 216-RICR-40-10-17.',
      atRisk: 'Your license expires soon or is not posted. Submit renewal before expiration. License expires December 31st annually per § 17.4.3(D). License must be posted per § 17.4.3(F).',
      nonCompliant: 'Operating without a valid RIDOH license violates RIGL § 23-17-4. Fine up to $5,000 and/or 1 year imprisonment per § 17.4.1(C)(1).',
      unknown: 'Verify your license status with RIDOH-CHFR at (401) 222-5960.',
    },
    stateNotes: [
      'License expires December 31st annually per § 17.4.3(D)',
      'Certificate of Need required for new agencies per § 17.4.1(B)',
      'License not transferable except with written RIDOH approval per § 17.4.3(E)',
      'Change of ownership requires Health Services Council review per § 17.4.2',
      'Operating without license: up to $5,000 fine and/or 1 year imprisonment per § 17.4.1(C)(1)',
    ],
  },

  {
    id: 'RI-A2',
    itemId: 'A2',
    stateCode: 'RI',
    title: 'Accreditation Requirement',
    description: 'Rhode Island requires accreditation within 2 years of initial licensure per 216-RICR-40-10-17 § 17.5.1(C)(8)',
    severity: 'high',
    regulationCode: '17.5.1(C)(8)',
    regulationName: 'Accreditation',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have accreditation from a nationally recognized accrediting body?',
        inputType: 'select',
        options: [
          { value: 'accredited', label: 'Yes - currently accredited' },
          { value: 'pending', label: 'Accreditation in process' },
          { value: 'new', label: 'No - licensed less than 2 years' },
          { value: 'no', label: 'No - licensed more than 2 years without accreditation' },
        ],
        required: true,
        helpText: 'Per § 17.5.1(C)(8): Accreditation must be achieved within 2 years of initial license and maintained.',
      },
      {
        id: 'q2',
        question: 'Which accrediting body?',
        inputType: 'select',
        options: [
          { value: 'achc', label: 'ACHC' },
          { value: 'chap', label: 'CHAP' },
          { value: 'joint_commission', label: 'The Joint Commission' },
          { value: 'other', label: 'Other nationally recognized body' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'accredited',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'accredited', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'new', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency maintains required accreditation per § 17.5.1(C)(8).',
      atRisk: 'Accreditation must be achieved within 2 years and maintained for licensure.',
      nonCompliant: 'Operating without required accreditation violates § 17.5.1(C)(8).',
      unknown: 'Verify accreditation status.',
    },
    stateNotes: [
      'Accreditation required within 2 years of initial license per § 17.5.1(C)(8)',
      'Must maintain accreditation to remain licensed in good standing',
      'Nationally recognized accrediting body required',
    ],
  },

  {
    id: 'RI-A3',
    itemId: 'A3',
    stateCode: 'RI',
    title: 'Administrator Qualifications',
    description: 'Rhode Island requires qualified administrator per 216-RICR-40-10-17 § 17.3(A)(2)',
    severity: 'high',
    regulationCode: '17.3(A)(2)',
    regulationName: 'Administrator',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet Rhode Island qualification requirements?',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician' },
          { value: 'health_admin', label: 'Training/experience in health service administration + 1 year supervisory experience in home care' },
          { value: 'rn_484', label: 'RN meeting 42 CFR Part 484 qualifications' },
          { value: 'none', label: 'Does not meet any qualification' },
        ],
        required: true,
        helpText: 'Per § 17.3(A)(2): Administrator must be a licensed physician, have health admin training with 1 year supervisory experience, or be an RN meeting 42 CFR 484.',
      },
      {
        id: 'q2',
        question: 'If administrator is not a nurse, do you have an RN Director of Nursing Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - Administrator is a nurse' },
        ],
        helpText: 'Per § 17.5.2(O)(2): If non-nurse administrator, nursing services must be under RN direction.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'includes', questionId: 'q1', value: ['physician', 'health_admin', 'rn_484'], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet § 17.3(A)(2) requirements.',
      atRisk: 'Administrator qualifications need verification.',
      nonCompliant: 'Administrator does not meet RIDOH qualification requirements.',
      unknown: 'Verify administrator qualifications per § 17.3(A)(2).',
    },
    stateNotes: [
      'Administrator must be: (a) licensed physician, (b) health admin training + 1 year supervisory experience, or (c) RN per 42 CFR 484',
      'If non-nurse administrator: nursing under RN Director per § 17.5.2(O)(2)',
      'Governing body appoints administrator per § 17.5.1(B)(1)',
    ],
  },

  {
    id: 'RI-A4',
    itemId: 'A4',
    stateCode: 'RI',
    title: 'Insurance Requirements',
    description: 'Rhode Island requires specific insurance minimums per 216-RICR-40-10-17 § 17.5.1(C)(2)',
    severity: 'high',
    regulationCode: '17.5.1(C)(2)',
    regulationName: 'Insurance',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain the required insurance coverages?',
        inputType: 'checklist',
        options: [
          { value: 'bonding', label: 'Employee bonding insurance - minimum $10,000 per loss' },
          { value: 'liability', label: 'Liability insurance - minimum $500,000 per occurrence' },
          { value: 'workers_comp', label: 'Workers\' compensation insurance' },
        ],
        required: true,
        helpText: 'Per § 17.5.1(C)(2): Bonding minimum $10,000 per loss, liability minimum $500,000 per occurrence.',
      },
      {
        id: 'q2',
        question: 'What is your current liability insurance coverage amount per occurrence?',
        inputType: 'select',
        options: [
          { value: '1m_plus', label: '$1,000,000 or more' },
          { value: '500k_999k', label: '$500,000 - $999,999' },
          { value: 'under_500k', label: 'Less than $500,000' },
        ],
        helpText: 'Minimum required is $500,000 per occurrence per § 17.5.1(C)(2).',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'under_500k', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets § 17.5.1(C)(2) minimums.',
      atRisk: 'Insurance coverage may be incomplete or below minimums.',
      nonCompliant: 'Insurance coverage does not meet Rhode Island minimums.',
      unknown: 'Verify insurance meets § 17.5.1(C)(2) requirements.',
    },
    stateNotes: [
      'Bonding insurance: minimum $10,000 per loss per § 17.5.1(C)(2)',
      'Liability insurance: minimum $500,000 per occurrence per § 17.5.1(C)(2)',
      'Workers\' compensation required per RIGL 28-29',
    ],
  },

  {
    id: 'RI-A5',
    itemId: 'A5',
    stateCode: 'RI',
    title: 'Professional Advisory Committee (Home Nursing Care)',
    description: 'Rhode Island requires Professional Advisory Committee for Home Nursing Care Providers per § 17.8.1(A)',
    severity: 'high',
    regulationCode: '17.8.1(A)',
    regulationName: 'Advisory Committee',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Are you licensed as a Home Nursing Care Provider (skilled nursing)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Home Care Provider only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a Professional Advisory Committee that meets requirements?',
        inputType: 'checklist',
        options: [
          { value: 'physician', label: 'Includes at least one physician' },
          { value: 'rn', label: 'Includes at least one RN' },
          { value: 'therapeutic', label: 'Includes representatives of each therapeutic service offered' },
          { value: 'non_employee', label: 'At least one member is not owner or employee' },
          { value: 'meets_2x', label: 'Meets at least twice per year' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.8.1(A): Committee must include physician, RN, and therapeutic service reps. At least one non-employee member. Meets 2x/year.',
      },
      {
        id: 'q3',
        question: 'Does the physician member attend at least one meeting per year?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.8.1(A)(6): Physician must attend at least one meeting per year.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      // Physician not attending meetings is AT_RISK per § 17.8.1(A)(6)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional Advisory Committee meets § 17.8.1(A) requirements.',
      atRisk: 'Professional Advisory Committee may not meet all requirements. Ensure physician attends at least one meeting per year per § 17.8.1(A)(6).',
      nonCompliant: 'Professional Advisory Committee does not meet requirements or is missing.',
      unknown: 'Verify Professional Advisory Committee composition and meeting frequency.',
    },
    stateNotes: [
      'Required for Home Nursing Care Providers only per § 17.8.1(A)',
      'Must include: physician, RN, therapeutic service reps per § 17.8.1(A)(1-2)',
      'At least one member must not be owner or employee per § 17.8.1(A)(4)',
      'Meets at least twice per year per § 17.8.1(A)(5)',
      'Physician attends at least one meeting per year per § 17.8.1(A)(6)',
      'Administrator is ex-officio member per § 17.8.1(A)(3)',
    ],
  },

  {
    id: 'RI-A6',
    itemId: 'A6',
    stateCode: 'RI',
    title: 'Quality Improvement Program',
    description: 'Rhode Island requires agency-wide quality improvement program per 216-RICR-40-10-17 § 17.5.1(E)',
    severity: 'high',
    regulationCode: '17.5.1(E)',
    regulationName: 'Quality Improvement',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Does your QI program include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'objectives', label: 'Program objectives - § 17.5.1(E)(2)(a)' },
          { value: 'oversight', label: 'Oversight responsibility (reports to governing body) - § 17.5.1(E)(2)(c)' },
          { value: 'agencywide', label: 'Agency-wide scope - § 17.5.1(E)(2)(d)' },
          { value: 'disciplines', label: 'All patient care disciplines involved - § 17.5.1(E)(2)(f)' },
          { value: 'monitoring', label: 'Methodology for monitoring/evaluating quality - § 17.5.1(E)(2)(g)' },
          { value: 'action', label: 'Priority setting and problem resolution - § 17.5.1(E)(2)(h)' },
          { value: 'effectiveness', label: 'Determination of action effectiveness - § 17.5.1(E)(2)(i)' },
          { value: 'documented', label: 'Documented QI plan review - § 17.5.1(E)(2)(j)' },
        ],
        required: true,
        helpText: 'Per § 17.5.1(E)(2): Written QI plan must include all listed elements.',
      },
      {
        id: 'q2',
        question: 'Do you evaluate all patient care services including contracted services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 17.5.1(E)(3): All patient care services including contractor services must be evaluated.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets § 17.5.1(E) requirements.',
      atRisk: 'Quality improvement program may be missing required elements.',
      nonCompliant: 'Quality improvement program is incomplete or missing.',
      unknown: 'Review QI program against § 17.5.1(E) requirements.',
    },
    stateNotes: [
      'Effective, ongoing, agency-wide QI program required per § 17.5.1(E)(1)',
      'Written plan of implementation required per § 17.5.1(E)(2)',
      'All patient care services evaluated including contractors per § 17.5.1(E)(3)',
      'Remedial action documented and submitted to governing body per § 17.5.1(E)(4)',
    ],
  },

  {
    id: 'RI-A7',
    itemId: 'A7',
    stateCode: 'RI',
    title: 'Emergency Preparedness',
    description: 'Rhode Island requires emergency preparedness plan per 216-RICR-40-10-17 § 17.5.1(G)',
    severity: 'high',
    regulationCode: '17.5.1(G)',
    regulationName: 'Emergency Prep',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Does your emergency preparedness plan include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Emergency preparedness policies and procedures - 42 CFR § 484.102' },
          { value: 'communications', label: 'Communications plan per federal/state/local laws' },
          { value: 'training', label: 'Training and testing program' },
          { value: 'updated_annually', label: 'Reviewed and updated annually - § 17.5.1(G)(1)' },
          { value: 'safety_experts', label: 'Developed with qualified safety/emergency management experts - § 17.5.1(G)(2)' },
          { value: 'backup', label: 'Back-up/contingency plans for internal systems failures - § 17.5.1(G)(3)' },
        ],
        required: true,
        helpText: 'Per § 17.5.1(G): Must include 42 CFR § 484.102 provisions and be reviewed annually.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan meets § 17.5.1(G) requirements.',
      atRisk: 'Emergency preparedness plan may be incomplete.',
      nonCompliant: 'Emergency preparedness plan is inadequate or missing.',
      unknown: 'Review emergency preparedness plan.',
    },
    stateNotes: [
      'Must include 42 CFR § 484.102 provisions per § 17.5.1(G)(1)(a)',
      'Reviewed and updated annually per § 17.5.1(G)(1)',
      'Developed with qualified experts per § 17.5.1(G)(2)',
      'Back-up plans for internal systems per § 17.5.1(G)(3)',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING
  // ============================================
  {
    id: 'RI-B1',
    itemId: 'B1',
    stateCode: 'RI',
    title: 'Criminal Records Check',
    description: 'Rhode Island requires national criminal records check with FBI fingerprints per RIGL § 23-17-34',
    severity: 'high',
    regulationCode: '23-17-34',
    regulationName: 'Background Check',
    agencyName: 'RI Attorney General - Bureau of Criminal Identification',
    agencyAbbr: 'BCI',
    agencyPhone: '(401) 274-4400',
    agencyUrl: 'https://riag.ri.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you initiate national criminal records checks (including FBI fingerprints) for all staff with routine patient contact?',
        inputType: 'select',
        options: [
          { value: 'before_hire', label: 'Yes - prior to employment' },
          { value: 'within_week', label: 'Yes - within one week of employment' },
          { value: 'later', label: 'Later than one week after employment' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per RIGL § 23-17-34(a): National criminal records check including FBI fingerprints must be initiated prior to or within one week of employment.',
      },
      {
        id: 'q2',
        question: 'Do you maintain evidence of criminal records checks on file for inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per RIGL § 23-17-34(e): Employer shall maintain on file, subject to RIDOH inspection, evidence that checks have been initiated and results.',
      },
      {
        id: 'q3',
        question: 'Do you have a process to address disqualifying information?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - documented process per § 17.5.1(C)(7)(c)' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 17.5.1(C)(7)(c): Must make timely judgment regarding continued employment upon disqualifying information.',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['before_hire', 'within_week'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'later', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // No process for disqualifying information is AT_RISK per § 17.5.1(C)(7)(c)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal records check procedures meet RIGL § 23-17-34 requirements.',
      atRisk: 'Criminal records checks may not be initiated within required timeframe or process for disqualifying information may be lacking.',
      nonCompliant: 'Criminal records check procedures do not comply with Rhode Island law.',
      unknown: 'Verify criminal records check procedures.',
    },
    stateNotes: [
      'National check with FBI fingerprints required per RIGL § 23-17-34(a)',
      'Initiated prior to or within one week of employment',
      'Required for positions with routine patient contact',
      'Maintain evidence on file for RIDOH inspection per RIGL § 23-17-34(e)',
      'Failure to maintain evidence = grounds for license revocation per RIGL § 23-17-34(e)',
      'Disqualifying information defined in RIGL § 23-17-37',
      'BCI fingerprinting: 4 Howard Avenue, Cranston (Pastore Complex)',
    ],
  },

  {
    id: 'RI-B2',
    itemId: 'B2',
    stateCode: 'RI',
    title: 'OIG/SAM Exclusion Verification',
    description: 'Federal requirement to verify staff not excluded from federal programs',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'Exclusion Verification',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How frequently do you check OIG exclusion list?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No checks' },
        ],
        required: true,
        helpText: 'OIG recommends monthly screening for all employees.',
      },
      {
        id: 'q2',
        question: 'Do you verify professional licenses via RIDOH database before hire?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 17.5.3(F): Must electronically verify licensure via RIDOH database.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Exclusion and license verification meets requirements.',
      atRisk: 'OIG checks should be conducted monthly, not just at hire.',
      nonCompliant: 'Required exclusion checks not being conducted.',
      unknown: 'Verify exclusion screening procedures.',
    },
    stateNotes: [
      'OIG LEIE check recommended monthly',
      'SAM.gov check for federal debarment',
      'Electronic license verification via RIDOH database per § 17.5.3(F)',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL RECORDS
  // ============================================
  {
    id: 'RI-C1',
    itemId: 'C1',
    stateCode: 'RI',
    title: 'Personnel File Requirements',
    description: 'Rhode Island requires specific personnel file documentation per 216-RICR-40-10-17 § 17.5.3(G)',
    severity: 'high',
    regulationCode: '17.5.3(G)',
    regulationName: 'Personnel Records',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee personnel file?',
        inputType: 'checklist',
        options: [
          { value: 'birth_i9', label: 'Birth certificate or I-9 verification - § 17.5.3(G)(1)' },
          { value: 'qualifications', label: 'Information on qualifications - § 17.5.3(G)(2)' },
          { value: 'training', label: 'Training and education records - § 17.5.3(G)(3)' },
          { value: 'license', label: 'Current licensure/certification - § 17.5.3(G)(4)' },
          { value: 'employment', label: 'Resume of previous employment - § 17.5.3(G)(5)' },
          { value: 'health', label: 'Health examination records (confidential) - § 17.5.3(G)(6)' },
          { value: 'complaints', label: 'Complaints and follow-up documentation - § 17.5.3(G)(7)' },
          { value: 'bci', label: 'Criminal records check findings (if after 7/26/1993) - § 17.5.3(G)(8)' },
          { value: 'references', label: 'Reference check prior to employment - § 17.5.3(G)(9)' },
          { value: 'orientation', label: 'Orientation, evaluation, in-service documentation - § 17.5.3(G)(10)' },
        ],
        required: true,
        helpText: 'Per § 17.5.3(G): Personnel files available at all times for RIDOH inspection.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per § 17.5.3(G).',
      atRisk: 'Some personnel files may be missing required documents.',
      nonCompliant: 'Personnel files missing critical required documentation.',
      unknown: 'Audit personnel files against § 17.5.3(G).',
    },
    stateNotes: [
      'Personnel files available for RIDOH inspection at all times per § 17.5.3(G)',
      'Health records kept confidential per § 17.5.3(G)(6)',
      'Annual evaluation reviewed with employee within 45 days per § 17.5.3(C)(1)',
    ],
  },

  {
    id: 'RI-C2',
    itemId: 'C2',
    stateCode: 'RI',
    title: 'Health Screening Requirements',
    description: 'Rhode Island requires pre-employment health screening per 216-RICR-40-10-17 § 17.5.3(K)',
    severity: 'high',
    regulationCode: '17.5.3(K)',
    regulationName: 'Health Screening',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do all staff with direct patient contact complete health screening before delivering services?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all staff before services' },
          { value: 'most', label: 'Most staff' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Per § 17.5.3(K)(1): Pre-employment health screening required upon hire and prior to delivering services.',
      },
      {
        id: 'q2',
        question: 'Is health screening conducted per 216-RICR-20-15-7 (Immunization, Testing, Health Screening for Health Care Workers)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / unsure' },
        ],
        helpText: 'Per § 17.5.3(K)(1): Health screening must comply with Part 20-15-7.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Health screening meets § 17.5.3(K) requirements.',
      atRisk: 'Some staff may not have completed health screening.',
      nonCompliant: 'Staff delivering services without required health screening.',
      unknown: 'Verify health screening compliance.',
    },
    stateNotes: [
      'Pre-employment health screening upon hire and before services per § 17.5.3(K)(1)',
      'Must comply with 216-RICR-20-15-7',
      'Required for anyone with direct or indirect patient contact',
      'Health records kept confidential per § 17.5.3(G)(6)',
    ],
  },

  {
    id: 'RI-C3',
    itemId: 'C3',
    stateCode: 'RI',
    title: 'Employee Audit Trail',
    description: 'Rhode Island requires audit trail for employees per 216-RICR-40-10-17 § 17.5.3(E)',
    severity: 'medium',
    regulationCode: '17.5.3(E)',
    regulationName: 'Audit Trail',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain an audit trail for each employee?',
        inputType: 'checklist',
        options: [
          { value: 'hire_date', label: 'Date of employment - § 17.5.3(E)(1)' },
          { value: 'assignments', label: 'Date(s), name(s), address(es) of each assignment - § 17.5.3(E)(2)' },
        ],
        required: true,
        helpText: 'Per § 17.5.3(E): Must maintain audit trail including employment date and all assignments.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 2, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 1, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee audit trail meets § 17.5.3(E) requirements.',
      atRisk: 'Employee audit trail may be incomplete.',
      nonCompliant: 'No employee audit trail maintained.',
      unknown: 'Verify audit trail documentation.',
    },
    stateNotes: [
      'Audit trail for each employee per § 17.5.3(E)',
      'Must include date of employment',
      'Must include dates, names, addresses of each assignment',
    ],
  },

  // ============================================
  // SECTION D: TRAINING REQUIREMENTS
  // ============================================
  {
    id: 'RI-D1',
    itemId: 'D1',
    stateCode: 'RI',
    title: 'In-Service Education Program',
    description: 'Rhode Island requires ongoing in-service education per 216-RICR-40-10-17 § 17.5.3(D)',
    severity: 'high',
    regulationCode: '17.5.3(D)',
    regulationName: 'In-Service Education',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Does your in-service program include required topics?',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'Orientation program for new staff' },
          { value: 'infection_control', label: 'Infection control measures' },
          { value: 'hipaa', label: 'HIPAA general provisions' },
          { value: 'abuse', label: 'Recognition and reporting of abuse, neglect, mistreatment, exploitation' },
          { value: 'skills', label: 'Continuing skills development' },
        ],
        required: true,
        helpText: 'Per § 17.5.3(D): In-service must include infection control, HIPAA, abuse reporting.',
      },
      {
        id: 'q2',
        question: 'Is the in-service program conducted on an ongoing basis?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - ongoing' },
          { value: 'no', label: 'No - sporadic or none' },
        ],
        helpText: 'Per § 17.5.3(D): In-service educational program shall be conducted on an ongoing basis.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'In-service education program meets § 17.5.3(D) requirements.',
      atRisk: 'In-service program may not cover all required topics.',
      nonCompliant: 'In-service education program is inadequate or missing.',
      unknown: 'Review in-service education program.',
    },
    stateNotes: [
      'Ongoing in-service program required per § 17.5.3(D)',
      'Must include: orientation, infection control, HIPAA, abuse reporting',
      'For staff providing direct care',
    ],
  },

  {
    id: 'RI-D2',
    itemId: 'D2',
    stateCode: 'RI',
    title: 'Homemaker Training',
    description: 'Rhode Island requires 25 hours homemaker training per 216-RICR-40-10-17 § 17.5.3(I)',
    severity: 'high',
    regulationCode: '17.5.3(I)',
    regulationName: 'Homemaker Training',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide homemaker services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Have ALL homemakers completed required training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - 20 hours classroom + 5 hours practical + passed exam' },
          { value: 'most', label: 'Most have completed training' },
          { value: 'no', label: 'No / not tracking' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.5.3(I)(1): 20 hours classroom + 5 hours practical experience. Must pass final exam per § 17.5.3(I)(3).',
      },
      {
        id: 'q3',
        question: 'Does homemaker training curriculum include required topics?',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'Agency orientation, policies, ethics, confidentiality - § 17.5.3(I)(2)(a)' },
          { value: 'human_needs', label: 'Basic human needs (children, elderly, disabled, terminal, mental illness) - § 17.5.3(I)(2)(b)' },
          { value: 'infection', label: 'Infection control, hand washing, universal precautions - § 17.5.3(I)(2)(c)' },
          { value: 'emergency', label: 'Emergency procedures - § 17.5.3(I)(2)(d)' },
          { value: 'home_care', label: 'Care of home (meals, nutrition, housekeeping, safety) - § 17.5.3(I)(2)(e)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.5.3(I)(2): Curriculum must include all listed topics.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // Curriculum missing required topics per § 17.5.3(I)(2)
      { condition: 'countAtLeast', questionId: 'q3', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q3', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Homemaker training meets § 17.5.3(I) requirements.',
      atRisk: 'Some homemakers may not have completed required training or curriculum may be incomplete.',
      nonCompliant: 'Homemakers providing services without required training or curriculum missing required topics.',
      unknown: 'Verify homemaker training documentation.',
    },
    stateNotes: [
      '20 hours classroom instruction per § 17.5.3(I)(1)',
      '5 hours practical experience in addition to classroom per § 17.5.3(I)(1)',
      'Must pass final examination per § 17.5.3(I)(3)',
      'Training approved by Director required',
      'Curriculum: orientation, human needs, infection control, emergency, home care',
    ],
  },

  {
    id: 'RI-D3',
    itemId: 'D3',
    stateCode: 'RI',
    title: 'Nursing Assistant Training',
    description: 'Rhode Island requires nursing assistant training per 216-RICR-40-05-22',
    severity: 'high',
    regulationCode: '17.6.6(A)',
    regulationName: 'CNA Training',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ nursing assistants / home health aides?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are ALL nursing assistants registered with RIDOH per RIGL Chapter 23-17.9?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all registered' },
          { value: 'most', label: 'Most registered' },
          { value: 'no', label: 'No / not verified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.3(A)(26): Nursing assistant must be registered per RIGL Chapter 23-17.9.',
      },
      {
        id: 'q3',
        question: 'Do nursing assistants meet training requirements per 216-RICR-40-05-22?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / unsure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.6.6(A): Same training requirements as Part 22 (Nursing Assistants, Medication Aides).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Nursing assistant training and registration meets requirements.',
      atRisk: 'Some nursing assistants may not be properly registered or trained.',
      nonCompliant: 'Nursing assistants working without required registration/training.',
      unknown: 'Verify nursing assistant credentials.',
    },
    stateNotes: [
      'Nursing assistants must be registered per RIGL Chapter 23-17.9',
      'Training per 216-RICR-40-05-22 per § 17.6.6(A)',
      '"Home health aide" = "nursing assistant" per § 17.3(A)(19)',
    ],
  },

  // ============================================
  // SECTION E: SUPERVISION REQUIREMENTS
  // ============================================
  {
    id: 'RI-E1',
    itemId: 'E1',
    stateCode: 'RI',
    title: 'Nursing Assistant Supervision',
    description: 'Rhode Island requires RN supervision of nursing assistants every 3 months per 216-RICR-40-10-17 § 17.6.6(B)',
    severity: 'high',
    regulationCode: '17.6.6(B)',
    regulationName: 'CNA Supervision',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ nursing assistants?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do RNs conduct initial in-home assessments before commencement of service?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always before or within 48 hours' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.6.6(B)(3)(d)(1): Initial in-home assessment prior to commencement, or within 48 hours if extraordinary circumstances.',
      },
      {
        id: 'q3',
        question: 'Are nursing assistants supervised on-site by an RN at least every 3 months?',
        inputType: 'select',
        options: [
          { value: 'per_regs', label: 'Yes - at least every 3 months per requirements' },
          { value: 'less_frequent', label: 'Less frequently than every 3 months' },
          { value: 'no', label: 'No on-site supervision' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.6.6(B)(3)(d)(1): Subsequent assessments and direct supervision no less than every 3 months.',
      },
      {
        id: 'q4',
        question: 'Is the plan of care reviewed and revised at least every 3 months?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.6.6(B)(3)(d)(3): Plan of care reviewed/revised no less than every 3 months.',
      },
    ],
    scoringRules: [
      // N/A - don't employ nursing assistants
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      // NON_COMPLIANT: No initial assessment or no supervision
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      // AT_RISK: Inconsistent assessments, infrequent supervision, or no plan review
      { condition: 'equals', questionId: 'q2', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'less_frequent', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'AT_RISK' },
      // COMPLIANT: All requirements met
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'per_regs' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Nursing assistant supervision meets § 17.6.6(B) requirements.',
      atRisk: 'Supervision may not meet 3-month frequency or plan of care review requirements.',
      nonCompliant: 'Nursing assistants not receiving required initial assessment or supervision.',
      unknown: 'Verify supervision procedures.',
    },
    stateNotes: [
      'RN supervision at least every 3 months per § 17.6.6(B)(3)(d)(1)',
      'Initial in-home assessment before service (or within 48 hours) per § 17.6.6(B)(3)(d)(1)',
      'Plan of care reviewed every 3 months per § 17.6.6(B)(3)(d)(3)',
      'On-site monitoring of performance every 3 months per § 17.6.6(B)(3)(d)(5)',
      'Supervision available during all operating hours per § 17.6.6(B)(1)',
    ],
  },

  {
    id: 'RI-E2',
    itemId: 'E2',
    stateCode: 'RI',
    title: 'Homemaker Supervision',
    description: 'Rhode Island requires qualified supervision of homemakers per 216-RICR-40-10-17 § 17.5.3(H)',
    severity: 'high',
    regulationCode: '17.5.3(H)',
    regulationName: 'Homemaker Supervision',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide homemaker services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is homemaker supervision provided by a qualified supervisor?',
        inputType: 'select',
        options: [
          { value: 'rn', label: 'RN licensed in Rhode Island' },
          { value: 'bachelor', label: 'Bachelor\'s degree in social work, home economics, gerontology, or related field' },
          { value: 'neither', label: 'Neither / no supervisor' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.5.3(H)(2): Supervised by RN or individual with bachelor\'s in social work, home economics, gerontology, or related field.',
      },
      {
        id: 'q3',
        question: 'Are in-home assessments conducted at least every 3 months?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.5.3(H)(2)(a): Initial assessment and assessments no less than every 3 months.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['rn', 'bachelor'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'neither', result: 'NON_COMPLIANT' },
      // Assessments not conducted every 3 months per § 17.5.3(H)(2)(a)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Homemaker supervision meets § 17.5.3(H) requirements.',
      atRisk: 'Homemaker supervision may not meet requirements.',
      nonCompliant: 'Homemaker services lack qualified supervision or in-home assessments not conducted every 3 months.',
      unknown: 'Verify homemaker supervision.',
    },
    stateNotes: [
      'Supervisor: RN or bachelor\'s in social work/home economics/gerontology/related per § 17.5.3(H)(2)',
      'Initial in-home assessment and every 3 months thereafter per § 17.5.3(H)(2)(a)',
      'Supervisor responsible for monitoring homemaker performance per § 17.5.3(H)(2)(b)',
      'Homemaker services per plan of care per § 17.5.3(H)(2)',
    ],
  },

  // ============================================
  // SECTION F: CLINICAL RECORDS
  // ============================================
  {
    id: 'RI-F1',
    itemId: 'F1',
    stateCode: 'RI',
    title: 'Clinical Record Requirements',
    description: 'Rhode Island requires comprehensive clinical records per 216-RICR-40-10-17 § 17.6.5(A)',
    severity: 'high',
    regulationCode: '17.6.5(A)',
    regulationName: 'Clinical Records',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do your clinical records include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'id', label: 'Identifying data (name, address, DOB, gender, admission date) - § 17.6.5(A)(1)' },
          { value: 'referral', label: 'Sources of patient referral - § 17.6.5(A)(2)' },
          { value: 'clinician', label: 'Attending clinician name, address, phone - § 17.6.5(A)(3)' },
          { value: 'poc', label: 'Original plan of care for services - § 17.6.5(A)(4)' },
          { value: 'contact', label: 'Documentation of each patient contact - § 17.6.5(A)(5)' },
          { value: 'changes', label: 'Changes in and reviews of plan of care - § 17.6.5(A)(6)' },
          { value: 'advance', label: 'Advance directive documentation if any - § 17.6.5(A)(7)' },
          { value: 'discharge', label: 'Discharge summaries - § 17.6.5(A)(8)' },
        ],
        required: true,
        helpText: 'Per § 17.6.5(A): Accurate and complete clinical records required.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records meet § 17.6.5(A) requirements.',
      atRisk: 'Clinical records may be missing required elements.',
      nonCompliant: 'Clinical records are incomplete.',
      unknown: 'Audit clinical records against § 17.6.5(A).',
    },
    stateNotes: [
      'Accurate and complete records per § 17.6.5(A)',
      'Filed in accessible location within agency',
      'Maintained per accepted professional standards',
    ],
  },

  {
    id: 'RI-F2',
    itemId: 'F2',
    stateCode: 'RI',
    title: 'Record Retention',
    description: 'Rhode Island requires 7 years record retention per 216-RICR-40-10-17 § 17.6.5(C)',
    severity: 'high',
    regulationCode: '17.6.5(C)',
    regulationName: 'Record Retention',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain clinical records after discharge?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer (meets requirement)' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per § 17.6.5(C): Records maintained at least 7 years following date of discharge.',
      },
      {
        id: 'q2',
        question: 'For minor patients, do you retain records for 7 years after they turn 18?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - no minor patients' },
        ],
        helpText: 'Per § 17.6.5(C)(1): Minimum 7 years after minor turns 18.',
      },
      {
        id: 'q3',
        question: 'Are records safeguarded against loss or unauthorized use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 17.6.5(C): Records safeguarded against loss or unauthorized use.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
      // Minor records not retained properly per § 17.6.5(C)(1)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // Records not safeguarded per § 17.6.5(C)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets 7-year requirement per § 17.6.5(C).',
      atRisk: 'Rhode Island requires 7 years. Update your retention policy.',
      nonCompliant: 'Record retention does not meet 7-year requirement, minor records not retained properly, or records not safeguarded.',
      unknown: 'Review record retention policy.',
    },
    stateNotes: [
      '7 years after discharge per § 17.6.5(C)',
      'Minors: 7 years after turning 18 per § 17.6.5(C)(1)',
      'Applies to paper and electronic records',
      'Safeguarded against loss or unauthorized use',
    ],
  },

  // ============================================
  // SECTION G: PLAN OF CARE
  // ============================================
  {
    id: 'RI-G1',
    itemId: 'G1',
    stateCode: 'RI',
    title: 'Plan of Care Requirements (Home Nursing Care)',
    description: 'Rhode Island requires physician-ordered plan of care per 216-RICR-40-10-17 § 17.8.1(C)',
    severity: 'high',
    regulationCode: '17.8.1(C)',
    regulationName: 'Plan of Care',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Are you a Home Nursing Care Provider (skilled nursing)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Home Care Provider only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the attending clinician\'s plan of care include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Pertinent diagnosis, mental status, level of consciousness, communication ability - § 17.8.1(C)(1)(a)(1)' },
          { value: 'services', label: 'Services, equipment, frequency, prognosis, functional limitations - § 17.8.1(C)(1)(a)(2)' },
          { value: 'nursing', label: 'Medical diagnosis, nursing assessment, therapeutic goals - § 17.8.1(C)(1)(a)(3)' },
          { value: 'surgical', label: 'Surgical procedures, complications, infections, prior diagnoses - § 17.8.1(C)(1)(a)(4)' },
          { value: 'orders', label: 'Attending clinician orders specifying procedures and modalities - § 17.8.1(C)(2)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.8.1(C)(1)(a): Plan of care prescribed by attending clinician must include these elements.',
      },
      {
        id: 'q3',
        question: 'Is the plan of care reviewed by attending clinician and agency at least every 60 days?',
        inputType: 'select',
        options: [
          { value: 'per_regs', label: 'Yes - every 60 days or as condition requires' },
          { value: 'less_frequent', label: 'Less frequently' },
          { value: 'no', label: 'No formal review process' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per § 17.8.1(C)(3): Reviewed no less than every 60 days beginning with start of care.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      // Plan of care missing required elements per § 17.8.1(C)(1)(a)
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'per_regs', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'less_frequent', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care meets § 17.8.1(C) requirements.',
      atRisk: 'Plan of care review may not meet 60-day requirement or may be missing required elements.',
      nonCompliant: 'Plan of care requirements not being met or missing critical elements.',
      unknown: 'Review plan of care procedures.',
    },
    stateNotes: [
      'Patients under care of attending clinician per § 17.8.1(C)(1)',
      'Plan reviewed every 60 days per § 17.8.1(C)(3)',
      'Not modified without attending clinician consent per § 17.8.1(C)(3)',
      'Copy of plan and service hours provided to patient per § 17.8.1(C)(5)',
    ],
  },

  // ============================================
  // SECTION H: ABUSE REPORTING
  // ============================================
  {
    id: 'RI-H1',
    itemId: 'H1',
    stateCode: 'RI',
    title: 'Mandatory Abuse Reporting',
    description: 'Rhode Island requires abuse reporting per RIGL § 42-66-8 and § 17.5.1(J)',
    severity: 'high',
    regulationCode: '42-66-8',
    regulationName: 'Abuse Reporting',
    agencyName: 'RI Office of Healthy Aging - Adult Protective Services',
    agencyAbbr: 'OHA-APS',
    agencyPhone: '(401) 462-0555',
    agencyUrl: 'https://oha.ri.gov/report-elder-abuse',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL staff trained on mandatory abuse reporting requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all staff trained on recognition and reporting' },
          { value: 'most', label: 'Most staff trained' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
        helpText: 'Per RIGL § 42-66-8: Any person with reasonable cause to believe a person 60+ has been abused, neglected, or exploited must report immediately.',
      },
      {
        id: 'q2',
        question: 'Do staff know to report elder abuse to the Office of Healthy Aging at (401) 462-0555?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure' },
        ],
        helpText: 'Per RIGL § 42-66-8: Report to Director of Office of Healthy Aging.',
      },
      {
        id: 'q3',
        question: 'Do you report facility-related abuse per RIGL § 23-17.8-2?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - on RIDOH forms' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 17.5.1(J): Duty to report per RIGL § 23-17.8-2 on RIDOH forms.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Staff don't know OHA reporting number per RIGL § 42-66-8
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      // Not reporting facility abuse per RIGL § 23-17.8-2
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse reporting training meets requirements.',
      atRisk: 'Some staff may lack abuse reporting training or not know OHA reporting number (401) 462-0555.',
      nonCompliant: 'Staff not trained on mandatory abuse reporting or facility abuse not reported on RIDOH forms.',
      unknown: 'Verify abuse reporting training.',
    },
    stateNotes: [
      'Any person must report suspected abuse of person 60+ per RIGL § 42-66-8',
      'Report to Office of Healthy Aging: (401) 462-0555',
      'Failure to report: fine up to $1,000 per RIGL § 42-66-8(d)',
      'Facility abuse reported on RIDOH forms per § 17.5.1(J)',
      'Reporter immunity per RIGL § 42-66-11',
    ],
  },

  // ============================================
  // SECTION I: PATIENT RIGHTS
  // ============================================
  {
    id: 'RI-I1',
    itemId: 'I1',
    stateCode: 'RI',
    title: 'Patient Rights',
    description: 'Rhode Island requires patient rights notification per 216-RICR-40-10-17 § 17.6.1',
    severity: 'high',
    regulationCode: '17.6.1',
    regulationName: 'Patient Rights',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written notice of patient rights before or during initial evaluation visit?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - in advance or during initial evaluation' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per § 17.6.1(B): Written notice in advance of furnishing care or during initial evaluation visit.',
      },
      {
        id: 'q2',
        question: 'Do you maintain signed copy of rights notice in patient record and patient\'s home?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - both locations' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per § 17.6.1(C): Copy signed by patient maintained in record AND in patient\'s home.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets § 17.6.1 requirements.',
      atRisk: 'Patient rights notification may be inconsistent.',
      nonCompliant: 'Patient rights not being provided as required.',
      unknown: 'Verify patient rights procedures.',
    },
    stateNotes: [
      'Rights per RIGL § 23-17-19.1 per § 17.6.1(A)',
      'Written notice before care or at initial evaluation per § 17.6.1(B)',
      'Signed copy in record AND patient\'s home per § 17.6.1(C)',
      'No charge for records needed for Social Security appeals per § 17.6.1(E)(1)',
    ],
  },

  // ============================================
  // SECTION J: ADDITIONAL REQUIREMENTS
  // ============================================
  {
    id: 'RI-J1',
    itemId: 'J1',
    stateCode: 'RI',
    title: 'Financial Interest Disclosure',
    description: 'Rhode Island requires disclosure of financial interests in referrals per 216-RICR-40-10-17 § 17.4.1(E)',
    severity: 'medium',
    regulationCode: '17.4.1(E)',
    regulationName: 'Financial Disclosure',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'When making referrals to entities in which you have financial interest, do you disclose this to patients?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - written disclosure at time of referral' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - no such financial interests' },
        ],
        required: true,
        helpText: 'Per § 17.4.1(E)(1): Must disclose financial interest in writing at time of referral.',
      },
      {
        id: 'q2',
        question: 'Do you offer patients a list of alternative providers?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'na',
        },
        helpText: 'Per § 17.4.1(E)(2): Must offer written list of alternative licensed/certified providers.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Financial interest disclosure meets § 17.4.1(E) requirements.',
      atRisk: 'Financial interest disclosure may be inconsistent.',
      nonCompliant: 'Financial interests not being disclosed as required.',
      unknown: 'Verify disclosure procedures.',
    },
    stateNotes: [
      'Written disclosure at time of referral per § 17.4.1(E)(1)',
      'Must inform patient of option to seek care elsewhere per § 17.4.1(E)(1)(b)',
      'Offer written list of alternative providers per § 17.4.1(E)(2)',
      'Non-compliance grounds for license action per § 17.4.1(F)',
    ],
  },

  {
    id: 'RI-J2',
    itemId: 'J2',
    stateCode: 'RI',
    title: 'Photo ID Badge Requirement',
    description: 'Rhode Island requires photo ID badges per 216-RICR-40-10-17 § 17.5.3(J)(3)',
    severity: 'medium',
    regulationCode: '17.5.3(J)(3)',
    regulationName: 'Photo ID',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff who examine, observe, or treat patients wear photo ID badges with required information?',
        inputType: 'checklist',
        options: [
          { value: 'first_name', label: 'First name - § 17.5.3(J)(3)(a)' },
          { value: 'license', label: 'Licensure/registration status if any - § 17.5.3(J)(3)(b)' },
          { value: 'languages', label: 'Fluency in languages other than English if any - § 17.5.3(J)(3)(c)' },
          { value: 'position', label: 'Staff position - § 17.5.3(J)(3)(d)' },
        ],
        required: true,
        helpText: 'Per § 17.5.3(J)(3): Photo ID badge must include first name, licensure status, language fluency, and staff position.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Photo ID badges meet § 17.5.3(J)(3) requirements.',
      atRisk: 'Photo ID badges may be missing required information.',
      nonCompliant: 'Photo ID badges do not meet requirements.',
      unknown: 'Review photo ID badge compliance.',
    },
    stateNotes: [
      'Applies to all who examine, observe, or treat patients including students per § 17.5.3(J)(3)',
      'Must include: first name, licensure status, language fluency, position',
      'Information must be reasonably legible',
    ],
  },

  {
    id: 'RI-J3',
    itemId: 'J3',
    stateCode: 'RI',
    title: 'Employee Classification',
    description: 'Rhode Island requires staff be classified as employees per 216-RICR-40-10-17 § 17.5.3(J)(4)',
    severity: 'high',
    regulationCode: '17.5.3(J)(4)',
    regulationName: 'Employee Status',
    agencyName: 'RIDOH-CHFR',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-5960',
    agencyUrl: 'https://health.ri.gov/licenses/',
    questions: [
      {
        id: 'q1',
        question: 'Are all direct care staff classified as W-2 employees (not independent contractors)?',
        inputType: 'select',
        options: [
          { value: 'all_w2', label: 'Yes - all W-2 employees' },
          { value: 'mixed', label: 'Mixed W-2 and 1099' },
          { value: 'all_1099', label: 'All 1099 contractors' },
        ],
        required: true,
        helpText: 'Per § 17.5.3(J)(4): Agency shall be considered employer and those supplied on temporary basis shall be employees not independent contractors.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all_w2', result: 'COMPLIANT' },
      { condition: 'equals', value: 'mixed', result: 'AT_RISK' },
      { condition: 'equals', value: 'all_1099', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee classification meets § 17.5.3(J)(4) requirements.',
      atRisk: 'Mixed classification may violate Rhode Island regulations.',
      nonCompliant: 'Staff classification does not meet Rhode Island requirements.',
      unknown: 'Review employee classification.',
    },
    stateNotes: [
      'Agency is employer for all purposes per § 17.5.3(J)(4)',
      'Staff supplied on temporary basis = employees, not independent contractors',
      'Subject to all state and federal employer/employee laws',
    ],
  },

  {
    id: 'RI-J4',
    itemId: 'J4',
    stateCode: 'RI',
    title: 'Communicable Disease Reporting',
    description: 'Rhode Island requires reporting of communicable diseases per 216-RICR-40-10-17 § 17.6.7',
    severity: 'high',
    regulationCode: '17.6.7',
    regulationName: 'Disease Reporting',
    agencyName: 'RIDOH Division of Disease Control',
    agencyAbbr: 'RIDOH',
    agencyPhone: '(401) 222-2577',
    agencyUrl: 'https://health.ri.gov/programs/detail.php?pgm_id=31',
    questions: [
      {
        id: 'q1',
        question: 'Do you promptly report communicable diseases to RIDOH Division of Disease Control?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per § 17.6.7(A): Report promptly cases of communicable diseases designated as "reportable diseases" per 216-RICR-30-05-1.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Communicable disease reporting procedures in place.',
      atRisk: 'Communicable disease reporting may be inconsistent.',
      nonCompliant: 'Not reporting communicable diseases as required.',
      unknown: 'Verify disease reporting procedures.',
    },
    stateNotes: [
      'Report to RIDOH Division of Disease Control per § 17.6.7(A)',
      'Reportable diseases per 216-RICR-30-05-1',
      'RIDOH Disease Control: (401) 222-2577',
    ],
  },
];

export default RHODEISLAND_QUESTIONS;